import { useState } from 'react'
import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { FormikCheckbox } from 'shared/components'
import {
  FormikTextfield,
  PhoneNumberInput,
  FormikSelect,
} from 'shared/components'
import { useCountryCode } from 'shared/hooks'
import { isPoland } from 'shared/utils'
import { getInitialValues } from './ContractorForm.constants'
import AddContractorDialogButtons from './AddContractorDialogButtons'
import {
  FormWrapper,
  DialogFieldWrapper,
  DialogRowWrapper,
  Wrapper,
  SectionWrapper,
  SectionTitle,
  RowOfTextfieldsWrapper,
  CheckboxWrapper,
  InputLabel,
  Label,
} from './ContractorForm.style'
import { ContractorFormProps, FormValues } from './ContractorForm.types'
import {
  useGetTaxNumberState,
  useValidationSchema,
  useAutofill,
} from './ContractorForm.utils'

const ContractorForm = ({
  onSubmit,
  phonePrefixesOptions,
  countriesOptions,
  contractor,
  onClose,
  idPrefix,
}: ContractorFormProps) => {
  const [initialValues, setInitialValues] = useState<FormValues>({
    ...(contractor || getInitialValues(countriesOptions, phonePrefixesOptions)),
  })
  const [countryId, setCountryId] = useState(initialValues.countryId)

  const countryCode = useCountryCode(countryId)

  const { t } = useTranslation()
  const { handleAutofill } = useAutofill(
    initialValues,
    setInitialValues,
    countryId as number
  )
  const validationSchema = useValidationSchema()
  const { isTaxFieldLoading } = useGetTaxNumberState()

  const currentSchema =
    validationSchema[countryCode] || validationSchema.default

  return (
    <Wrapper>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={currentSchema}
        enableReinitialize
        validateOnChange
        validateOnMount
        initialErrors={{ taxNumberError: null }}
      >
        {({ values, setFieldValue, isSubmitting, isValid }) => (
          <Form>
            <FormWrapper>
              <SectionWrapper>
                <SectionTitle>{t('basicInformation')}</SectionTitle>
                <DialogRowWrapper>
                  <FormikSelect
                    label={<Label>{t('contractor.country')}</Label>}
                    name="countryId"
                    options={countriesOptions}
                    onChange={val => setCountryId(val as number)}
                    fullWidth
                    disabled={isSubmitting}
                    labelComponent={InputLabel}
                  />
                </DialogRowWrapper>
                <DialogRowWrapper>
                  <FormikTextfield
                    disabled={isSubmitting}
                    name="taxNumber"
                    validateWithoutBlur
                    isLoading={isTaxFieldLoading}
                    label={<Label>{t('contractor.taxNumber')}</Label>}
                    labelComponent={InputLabel}
                    onChange={e => {
                      setFieldValue('taxNumber', e.target.value)
                      handleAutofill(e.target.value, countryCode)
                    }}
                  />
                </DialogRowWrapper>
                <DialogRowWrapper>
                  <FormikTextfield
                    disabled={isSubmitting}
                    name="name"
                    placeholder={t('contractor.fullNameOfCompany')}
                    labelComponent={InputLabel}
                    label={<Label>{t('contractor.name')}</Label>}
                  />
                </DialogRowWrapper>
                <DialogRowWrapper>
                  {isPoland(countryCode) && (
                    <FormikTextfield
                      disabled={isSubmitting}
                      name="nationalBusinessRegistryNumber"
                      labelComponent={InputLabel}
                      label={
                        <Label required={false}>
                          {t('contractor.nationalBusinessRegistryNumber')}
                        </Label>
                      }
                    />
                  )}
                </DialogRowWrapper>
                <DialogRowWrapper>
                  <FormikTextfield
                    disabled={isSubmitting}
                    name="street"
                    labelComponent={InputLabel}
                    label={<Label>{t('contractor.street')}</Label>}
                  />
                </DialogRowWrapper>
                <RowOfTextfieldsWrapper>
                  <DialogFieldWrapper small>
                    <FormikTextfield
                      disabled={isSubmitting}
                      name="buildingNumber"
                      wrapError
                      labelComponent={InputLabel}
                      label={<Label>{t('contractor.buildingNumber')}</Label>}
                    />
                  </DialogFieldWrapper>
                  <DialogFieldWrapper small>
                    <FormikTextfield
                      disabled={isSubmitting}
                      name="apartmentNumber"
                      labelComponent={InputLabel}
                      label={
                        <Label required={false}>
                          {t('contractor.apartmentNumber')}
                        </Label>
                      }
                    />
                  </DialogFieldWrapper>
                </RowOfTextfieldsWrapper>
                <RowOfTextfieldsWrapper>
                  <DialogFieldWrapper small>
                    <FormikTextfield
                      disabled={isSubmitting}
                      name="zipCode"
                      wrapError
                      labelComponent={InputLabel}
                      label={<Label>{t('contractor.zipCode')}</Label>}
                    />
                  </DialogFieldWrapper>
                  <DialogFieldWrapper>
                    <FormikTextfield
                      disabled={isSubmitting}
                      name="city"
                      labelComponent={InputLabel}
                      label={<Label>{t('contractor.city')}</Label>}
                    />
                  </DialogFieldWrapper>
                </RowOfTextfieldsWrapper>
              </SectionWrapper>
              <SectionWrapper>
                <SectionTitle>{t('contact.details')}</SectionTitle>
                <DialogRowWrapper>
                  <PhoneNumberInput
                    disabled={isSubmitting}
                    selectName="phonePrefix"
                    inputName="phoneNumber"
                    label={t('mobilePhoneNumber')}
                    prefixOptions={phonePrefixesOptions}
                    labelComponent={InputLabel}
                  />
                </DialogRowWrapper>
                <DialogRowWrapper>
                  <FormikTextfield
                    disabled={isSubmitting}
                    name="emailAddress"
                    labelComponent={InputLabel}
                    label={t('contractor.email')}
                  />
                </DialogRowWrapper>
                <CheckboxWrapper>
                  <FormikCheckbox
                    name="isContactActive"
                    checked={values.isContactActive}
                    onChange={() =>
                      setFieldValue('isContactActive', !values.isContactActive)
                    }
                    text={t('contact.useForNotifications')}
                    disabled={!values.phoneNumber && !values.emailAddress}
                  />
                </CheckboxWrapper>
              </SectionWrapper>
            </FormWrapper>
            <AddContractorDialogButtons
              isLoading={isSubmitting}
              onCancel={onClose}
              idPrefix={idPrefix}
              isSubmitDisabled={!isValid}
            />
          </Form>
        )}
      </Formik>
    </Wrapper>
  )
}

export default ContractorForm
